import React from 'react';
import {Modal} from "react-bootstrap";
import {GoPencil} from 'react-icons/go';
import {useDispatch} from "react-redux";
import Dinero, {Currency} from "dinero.js";
import {
  DeclinedExternalQuotationItem,
  ExternalQuotationItem,
  QuotationItemStatuses,
  QuotationItemUpdate
} from "../../types/models/quotation_item";
import {getExternalQuotationItemStatusDisplayMessage, isResolved} from "../../lib/quotation_item";
import styles from '../../styles/external_quotation_view_modal.module.scss';
import classNames from "classnames";
import {genericTextChange} from "../../utils/forms";
import CartQuantitySelect from "../cart/quantity_select";
import {actions} from "../../store/actions";
import Api from '../../service/api';
import {isEmpty} from "lodash";
import Link from "next/link";
import {useRegionCountryCode} from "../../hooks/region";
import {formatDinero, useAppCurrency} from "../../service/currency_converter";
import {currencyPrecision} from "../../constants/currency_precision";

interface ViewExternalQuotationModalProps {
  show: boolean;
  onCancel: () => void;
  onChange: (deleted: boolean, updated?: ExternalQuotationItem) => void;
  externalQuotationItem?: ExternalQuotationItem;
}

export default function ViewExternalQuotationModal({externalQuotationItem, show, onCancel, onChange}: ViewExternalQuotationModalProps) {
  const [isEditingInstructions, setIsEditingInstructions] = React.useState(false);
  const [editedInstructions, setEditedInstructions] = React.useState('');
  const [isInputLocked, setIsInputLocked] = React.useState(false);

  const dispatch = useDispatch();
  const appCurrency = useAppCurrency();
  const regionInfo = useRegionCountryCode();

  const editInstructions = React.useCallback(() => {
    setIsEditingInstructions(true);
    if (externalQuotationItem) {
      setEditedInstructions(externalQuotationItem.instructions);
    }
  }, [externalQuotationItem]);

  const stopEditingInstructions = React.useCallback(() => setIsEditingInstructions(false), []);
  const unlockInputs = React.useCallback(() => setIsInputLocked(false), []);

  const handleSaveQuotationItem = React.useCallback((changes: QuotationItemUpdate) => {
    const item = externalQuotationItem!;
    if (changes.instructions === item.instructions) {
      delete changes.instructions;
    }

    if (item.quantity === changes.quantity) {
      delete changes.quantity;
    }

    if (isEmpty(changes)) {
      return;
    }

    setIsInputLocked(true);
    if (changes.quantity === 0) {
      Api.quotationItem.delete(item._id)
        .then(() => {
          onChange(true);
          dispatch(actions.quotationItem.removeItem(item._id));
        })
        .finally(unlockInputs);
    } else {
      Api.quotationItem.update(item._id, changes)
        .then(() => {
          onChange(false, Object.assign({}, item, changes));
          dispatch(actions.quotationItem.setItem(changes, item._id));
        })
        .finally(unlockInputs);
    }
  }, [externalQuotationItem]);

  const handleSetQuantity = React.useCallback((currentQuantity: number) => {
    handleSaveQuotationItem({
      quantity: currentQuantity
    });
  }, [handleSaveQuotationItem]);

  const handleSaveInstructions = React.useCallback(() => {
    handleSaveQuotationItem({
      instructions: editedInstructions
    });
    stopEditingInstructions();
  }, [editedInstructions, handleSaveQuotationItem, stopEditingInstructions]);

  const quotedPrice = React.useMemo(() => {
    if (!externalQuotationItem || !isResolved(externalQuotationItem)) {
      return null;
    }

    const currency = appCurrency ?? 'SGD';
    return Dinero({
      currency: currency as Currency,
      amount: externalQuotationItem.statusData.quotedPrice[regionInfo.countryCode],
      precision: currencyPrecision[currency]
    });
  }, [externalQuotationItem, appCurrency, regionInfo.isLoading, regionInfo.countryCode]);

  const showModal = show && !!externalQuotationItem;
  return (
    <Modal
      show={showModal}
      onHide={onCancel}
      centered
    >
      <Modal.Header closeButton>
        {externalQuotationItem && (
          <div>
            <div className={styles.title}>
              {getExternalQuotationItemStatusDisplayMessage(externalQuotationItem)}
            </div>
            <div className={styles.subtitle}>
              QUOTE ID #{externalQuotationItem.referenceNumber}
            </div>
          </div>
        )}
      </Modal.Header>
      <Modal.Body>
        {externalQuotationItem && (
          <>
            <div className="media mb-2">
              <div className="media-body">
                <div className="font-weight-semibold">
                  <a href={externalQuotationItem.productSourceData.link} target="_blank" rel="no-referrer">
                    {externalQuotationItem.name}
                  </a>
                </div>
              </div>
              <div className={`align-self-center ${styles.quotationItemImageContainer}`}>
                <img src={externalQuotationItem.mainImage} alt="quotation item image" />
              </div>
            </div>

            {quotedPrice && (
              <div className={classNames(styles.row, 'mb-2')}>
                <div className={styles.label}>Quoted Price</div>
                <div className={styles.content}>
                  {formatDinero(quotedPrice)}
                </div>
              </div>
            )}


            <div className={classNames(styles.row, 'mb-2')}>
              <div className={styles.label}>Quantity</div>
              <div className={styles.content}>
                <CartQuantitySelect
                  value={externalQuotationItem.quantity}
                  onChange={handleSetQuantity}
                  disabled={isInputLocked}
                  maxQuantity={0}
                  minQuantity={0}
                />
              </div>
            </div>

            <div className={classNames(styles.row, 'mb-2')}>
              <div className={styles.label}>
                Instructions

                {!isEditingInstructions && (
                  <span className={classNames(styles.clickable, 'ml-1')} onClick={editInstructions}>
                    <GoPencil />
                  </span>
                )}
              </div>
              <div className={classNames(styles.content, {
                'w-100': isEditingInstructions
              })}>
                {!isEditingInstructions ? externalQuotationItem.instructions : (
                  <>
                    <div className="form-group">
                      <label className="form-label">Edit Message</label>
                      <textarea
                        className="form-control"
                        onChange={genericTextChange(setEditedInstructions)}
                        value={editedInstructions}
                      />
                    </div>

                    <div className="form-group text-right">
                      <button className="btn btn-sm btn-success mr-1" onClick={handleSaveInstructions}>
                        Save
                      </button>
                      <button className="btn btn-sm btn-secondary" onClick={stopEditingInstructions}>
                        Cancel
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>

            {externalQuotationItem.status === QuotationItemStatuses.declined && (
              <div className={classNames(styles.row, 'mb-2')}>
                <div className={styles.label}>
                  Reason for decline
                </div>
                <div className={styles.content}>
                  {(externalQuotationItem as DeclinedExternalQuotationItem).statusData.declineReason}
                </div>
              </div>
            )}

            <div className="text-right">
              <Link
                href={externalQuotationItem ? `/cart?itemId=${externalQuotationItem._id}` : '/cart'}
                className="btn btn-primary">
                Checkout
              </Link>
            </div>
          </>
        )}
      </Modal.Body>
    </Modal>
  );
}